// TopBar.tsx
import { Popover, PopoverContent, PopoverTrigger } from '@heroui/react';
import { Timestamp } from 'firebase/firestore';
import {
  AlignLeft,
  ArrowLeft,
  ArrowRight,
  Bell,
  Mail,
  MonitorSmartphone,
  Smartphone,
} from 'lucide-react';
import React, { FC, useCallback, useEffect, useMemo, useState, useTransition } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import { ReactSVG } from 'react-svg';
import { useTheme } from 'styled-components';

import { useOrganizationState, useUserState } from '@/state';
import { LocaleLink, useLocaleNavigate } from '@/utils/locale';

import shortLogo from '../../../assets/images/short-hs-logo.svg?url';
import stefanAvatar from '../../../assets/stefan.jpg';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { useUnreadCounts } from '../../../hooks/useUnreadCounts';
import { useHelpAiChat } from '../../../views/Dashboard/components/HelpAiChat';
import { useGetMyAccountData } from '../../../views/UserProfile/queries';
import { DrawerContainer } from '../../DrawerContainer';
import { GuestAppPreview } from '../../GuestAppPreview';
import { Notification } from '../../Notification';
import { Spinner } from '../../Spinner';
import { Breadcrumbs } from '../Breadcrumbs';
import { UserMenu } from '../UserMenu/UserMenu';
import { HelpDrawerContent } from './HelpDrawerContent';
import {
  AskAiAvatar,
  AskAiButton,
  AskAiLabel,
  DisabledMobileIcon,
  IconCircleButton,
  LeftNav,
  MobileLogo,
  MobileNavigationHeadline,
  NavArrowButton,
  NavArrows,
  NotificationBadge,
  RightNav,
  TestModeBadge,
  TestModeCountdown,
  TestModeLabel,
  TopBarWrapper,
} from './TopBar.styled';

interface TopBarProps {
  showMobileNavigation: () => void;
}

const getTestModeCountdown = (expirationDate: Timestamp | null): string | null => {
  if (!expirationDate) return null;

  const now = Date.now();
  const expMs = expirationDate.toMillis();
  const diff = expMs - now;

  if (diff <= 0) return null;

  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

  if (days > 0) return `${days}d ${hours}h`;
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  return `${hours}h ${minutes}m`;
};

const getTestModeUrgency = (expirationDate: Timestamp | null): 'normal' | 'warning' | 'critical' => {
  if (!expirationDate) return 'normal';

  const now = Date.now();
  const expMs = expirationDate.toMillis();
  const diffMs = expMs - now;

  if (diffMs <= 0) return 'critical';

  const ONE_DAY = 1000 * 60 * 60 * 24;
  if (diffMs <= ONE_DAY) return 'critical';
  if (diffMs <= 3 * ONE_DAY) return 'warning';
  return 'normal';
};

export const TopBar: FC<TopBarProps> = (props) => {
  const { showMobileNavigation } = props;
  const { logout, currentUser } = useUserState();
  const { organizationId, plan, testExpirationDate, isDisabled, webAppUrl } = useOrganizationState();
  const isTestMode = plan === 'TEST';
  const navigate = useLocaleNavigate();
  // Surowy navigate z react-router — do cofania/przewijania historii
  // (delta), gdzie prefiks locale nie ma zastosowania.
  const historyNavigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();
  const [, startTransition] = useTransition();
  const [isGuestAppOpen, setGuestAppOpen] = useState(false);
  const theme = useTheme();
  const { t } = useTranslation();
  const iconColor = theme.mode === 'dark' ? '#e5e7eb' : '#374151';
  const isMobile = useIsMobile();

  // Śledzenie pozycji w historii — do wyszarzania strzałek wstecz/dalej.
  const [historyPos, setHistoryPos] = useState(0);
  const [historySize, setHistorySize] = useState(1);

  useEffect(() => {
    const idx = (window.history.state as { idx?: number } | null)?.idx;
    if (navigationType === 'PUSH') {
      const next = typeof idx === 'number' ? idx : historyPos + 1;
      setHistoryPos(next);
      setHistorySize(next + 1);
      return;
    }
    if (navigationType === 'POP' && typeof idx === 'number') {
      setHistoryPos(idx);
      return;
    }
    if (typeof idx === 'number') {
      setHistoryPos(idx);
      setHistorySize((size) => Math.max(size, idx + 1));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- tylko przy zmianie lokalizacji / typu nawigacji
  }, [location.key, navigationType]);

  const canGoBack = historyPos > 0;
  const canGoForward = historyPos < historySize - 1;

  const { data: docUser, isLoading } = useGetMyAccountData(currentUser?.uid);

  const {
    unreadEmails,
    unreadNotifications,
    unreadChatCount,
  } = useUnreadCounts(currentUser?.uid, organizationId);

  const [isMobileAppPopoverOpen, setIsMobileAppPopoverOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [isHelpDrawerOpen, setIsHelpDrawerOpen] = useState(false);

  const { isOpen: isChatOpen, toggle: toggleChat } = useHelpAiChat();

  const testCountdown = useMemo(
    () => getTestModeCountdown(testExpirationDate),
    [testExpirationDate],
  );

  const testUrgency = useMemo(
    () => getTestModeUrgency(testExpirationDate),
    [testExpirationDate],
  );

  const hasExpiredTrial = useMemo(() => {
    if (!testExpirationDate) return false;
    return testExpirationDate.toMillis() < Date.now();
  }, [testExpirationDate]);

  const showTrialBadge = useMemo(() => (isTestMode || hasExpiredTrial) && !isMobile, [isTestMode, hasExpiredTrial, isMobile]);

  useEffect(() => {
    setIsNotificationsOpen(false);
  }, [location.pathname]);

  const createPopoverHandler = useCallback((
    setOpen: (value: boolean) => void,
    setHasOpened?: (value: boolean) => void,
  ) => (open: boolean) => {
    if (open && setHasOpened) {
      setHasOpened(true);
    }

    startTransition(() => {
      if (!open) {
        setTimeout(() => setOpen(false), 100);
      } else {
        setOpen(open);
      }
    });
  }, [startTransition]);

  const handleNotificationsOpenChange = useMemo(
    () => createPopoverHandler(setIsNotificationsOpen),
    [createPopoverHandler],
  );

  // Ikona dzwoneczka: okrągły button (28×28) z szarym tłem i ramką —
  // wizualnie spójny z avatarem użytkownika obok (`UserMenu.AvatarWrapper`).
  // Badge ilości wystaje nad prawym górnym rogiem, gdy są nieprzeczytane.
  // Renderujemy jako natywny <button> żeby HeroUI PopoverTrigger nie
  // dorzucał własnego opakowania — IconCircleButton jest już buttonem.
  const notificationsTrigger = useMemo(
    () => (
      <IconCircleButton type="button" data-tour="notifications" aria-label={t('alerts.notifications')}>
        <Bell size={18} strokeWidth={2} />
        {unreadNotifications > 0 && (
          <NotificationBadge>
            {unreadNotifications > 99 ? '99+' : unreadNotifications}
          </NotificationBadge>
        )}
      </IconCircleButton>
    ),
    [unreadNotifications, t],
  );

  return (
    <TopBarWrapper>
      <LeftNav>
        <MobileNavigationHeadline onClick={showMobileNavigation}>
          <AlignLeft size={20} color={iconColor} />
        </MobileNavigationHeadline>
        <MobileLogo>
          <LocaleLink to="/">
            <ReactSVG src={shortLogo} />
          </LocaleLink>
        </MobileLogo>

        <NavArrows>
          <NavArrowButton
            type="button"
            aria-label={t('topbar.goBack', 'Wstecz')}
            disabled={!canGoBack}
            onClick={() => historyNavigate(-1)}
          >
            <ArrowLeft size={18} strokeWidth={2} />
          </NavArrowButton>
          <NavArrowButton
            type="button"
            aria-label={t('topbar.goForward', 'Dalej')}
            disabled={!canGoForward}
            onClick={() => historyNavigate(1)}
          >
            <ArrowRight size={18} strokeWidth={2} />
          </NavArrowButton>
        </NavArrows>

        <Breadcrumbs />
      </LeftNav>

      <RightNav>

        {isTestMode && (
          <Popover
            placement="bottom"
            radius="sm"
            isOpen={isMobileAppPopoverOpen}
            onOpenChange={setIsMobileAppPopoverOpen}
            shouldBlockScroll={false}
            classNames={{
              content: 'border border-gray-200 dark:border-[#374151] rounded-md p-3',
            }}
          >
            <PopoverTrigger>
              <DisabledMobileIcon>
                <Smartphone size={16} color={iconColor} strokeWidth={1.75} />
              </DisabledMobileIcon>
            </PopoverTrigger>
            <PopoverContent
              className="dark:bg-[#1a1f26]"
              style={{
                boxShadow: '0 4px 12px 0 rgba(0, 0, 0, 0.08)',
              }}
            >
              <div style={{ maxWidth: '250px', padding: '8px' }}>
                <p style={{ margin: '0 0 8px 0', fontSize: '13px', color: theme.mode === 'dark' ? '#e5e5e5' : '#374151' }}>
                  {t('topbar.mobileAppDisabledInTrial')}
                </p>
              </div>
            </PopoverContent>
          </Popover>
        )}

        {/* Plakietka trialu jest już tylko informacją — ekran zmiany planu
            został usunięty, więc nie ma dokąd kierować. */}
        {showTrialBadge && testCountdown && (
          <TestModeBadge $urgency={testUrgency}>
            <TestModeLabel $urgency={testUrgency}>{t('layout.trialPeriodRemaining')}</TestModeLabel>
            <TestModeCountdown $urgency={testUrgency}>{testCountdown}</TestModeCountdown>
          </TestModeBadge>
        )}

        {/* Ikona pomocy (?) — ukryta na życzenie. Drawer pomocy zostaje
            dostępny przez stan isHelpDrawerOpen (np. z UserMenu). */}
        {/* <IconCircleButton
          type="button"
          aria-label={t('helpDrawer.title')}
          onClick={() => setIsHelpDrawerOpen(true)}
        >
          <CircleHelp size={18} strokeWidth={2} />
        </IconCircleButton> */}

        {/* CTA asystenta AI „Stefan" — otwiera/zamyka dokowany panel czatu.
            Zastąpił pływający avatar przypięty do prawej krawędzi. */}
        {!isDisabled && (
          <AskAiButton
            type="button"
            $active={isChatOpen}
            onClick={toggleChat}
            aria-pressed={isChatOpen}
            aria-label={t('helpAiChat.ask', 'Zapytaj')}
            data-tour="ai-chat-bubble"
          >
            <AskAiAvatar src={stefanAvatar} alt="Stefan AI" />
            <AskAiLabel>{t('helpAiChat.ask', 'Zapytaj')}</AskAiLabel>
          </AskAiButton>
        )}

        {/* Podgląd aplikacji gościa w obudowie telefonu. Bez adresu nie ma czego
            pokazywać, więc przycisk się wtedy nie pojawia. */}
        {webAppUrl && (
          <IconCircleButton
            type="button"
            aria-label={t('topbar.guestApp', 'Hotelspot Guest App preview')}
            onClick={() => setGuestAppOpen(true)}
          >
            <MonitorSmartphone size={18} strokeWidth={2} />
          </IconCircleButton>
        )}

        {/* Koperta — wiadomości. Plakietka z liczbą nieprzeczytanych, tak samo jak przy
            dzwonku: bez niej nowa wiadomość w skrzynce nie dawała żadnego znaku życia.
            Liczbę liczy `useUnreadCounts` z `inmails` bieżącej organizacji. */}
        <IconCircleButton
          type="button"
          data-tour="mails"
          aria-label={unreadEmails > 0
            ? `${t('routes.mails')} (${unreadEmails})`
            : t('routes.mails')}
          onClick={() => navigate('/wiadomosci')}
        >
          <Mail size={18} strokeWidth={2} />
          {unreadEmails > 0 && (
            <NotificationBadge>
              {unreadEmails > 99 ? '99+' : unreadEmails}
            </NotificationBadge>
          )}
        </IconCircleButton>

        {/* Notifications Popover */}
        <Popover
          placement={isMobile ? 'bottom' : 'bottom-end'}
          size="lg"
          radius="sm"
          isOpen={isNotificationsOpen}
          onOpenChange={handleNotificationsOpenChange}
          shouldBlockScroll={false}
          shouldCloseOnBlur
          triggerScaleOnOpen={false}
          disableAnimation
          classNames={{
            content: 'border border-gray-200 dark:border-[#374151] rounded-md',
          }}
        >
          <PopoverTrigger className="rounded-full">{notificationsTrigger}</PopoverTrigger>
          <PopoverContent
            className="p-0 dark:bg-[#1a1f26]"
            style={{
              boxShadow: '0 4px 12px 0 rgba(0, 0, 0, 0.08)',
            }}
          >
            {isNotificationsOpen && <Notification showFooter showTitle daysLimit={14} />}
          </PopoverContent>
        </Popover>

        {/* Chat Icon - hidden */}
        {/* {chatTrigger} */}

        {/* Ramka montuje się dopiero z otwarciem szuflady i ginie z jej zamknięciem —
            to druga pełna aplikacja z własnymi nasłuchami Firestore, więc nie ma
            prawa działać w tle. */}
        <DrawerContainer
          title={t('topbar.guestApp', 'Hotelspot Guest App preview')}
          subtitle={t(
            'guestAppPreview.description',
            'Podgląd aplikacji, którą widzą goście Twojego hotelu, z najnowszej dystrybucji.',
          )}
          isOpen={isGuestAppOpen}
          size="xl"
          backdrop="opaque"
          onClose={() => setGuestAppOpen(false)}
          onOpenChange={() => setGuestAppOpen(false)}
          highZIndex
          /* Telefon dobiera wysokość do okna, więc szuflada nie ma czego przewijać.
             `!` jest konieczne: motyw HeroUI daje treści szuflady `overflow-y-auto`,
             a ta klasa wypada w arkuszu PÓŹNIEJ niż zwykłe `overflow-hidden` i
             wygrywała na osi pionowej. */
          bodyClassName="overflow-hidden !overflow-y-hidden"
        >
          {isGuestAppOpen && <GuestAppPreview url={webAppUrl || ''} />}
        </DrawerContainer>

        <DrawerContainer
          title={t('helpDrawer.title')}
          isOpen={isHelpDrawerOpen}
          size="xl"
          backdrop="opaque"
          onClose={() => setIsHelpDrawerOpen(false)}
          onOpenChange={() => setIsHelpDrawerOpen(false)}
          highZIndex
        >
          <HelpDrawerContent />
        </DrawerContainer>

        {isLoading ? (<Spinner size="sm" variant="default" />) : (
          <div data-tour="profile">
            <UserMenu
              userName={docUser?.name || ''}
              userEmail={docUser?.email || ''}
              userAvatar={docUser?.profilePicture}
              onProfileClick={() => navigate('/my-profile')}
              onSettingsClick={() => navigate('/settings')}
              onHelpClick={() => setIsHelpDrawerOpen(true)}
              onLogout={() => logout()}
            />
          </div>
        )}

      </RightNav>
    </TopBarWrapper>
  );
};
